"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Film, GripVertical, HardDrive, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// Types - exported for reusability
export type KanbanApprovalStatus = "pending" | "approved" | "rejected";

export interface KanbanTask {
  id: string;
  title: string;
  description?: string;
  labels?: string[];
  assignee?: string;
  imageUrl?: string;
  createdAt?: string;
  // Pipeline extended fields
  process?: string;
  editorUserId?: string;
  editorName?: string;
  contentTypes?: string[];
  scheduledAt?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  script?: string;
  // Approval gate (Sprint 3, transcript 2026-05)
  approvalStatus?: KanbanApprovalStatus;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  // Scheduler integration (VP-107) — link a pipeline card to a real scheduled post.
  scheduledPostId?: string;
  scheduledPlatform?: string;
  publishedPostId?: string;
  publishedAt?: string;
}

export interface KanbanColumn {
  id: string;
  title: string;
  tasks: KanbanTask[];
}

export interface KanbanBoardProps {
  columns: KanbanColumn[];
  onColumnsChange?: (columns: KanbanColumn[]) => void;
  onTaskMove?: (taskId: string, fromColumnId: string, toColumnId: string) => void;
  onTaskAdd?: (columnId: string, title: string) => void;
  onTaskClick?: (task: KanbanTask, columnId: string) => void;
  onTaskDelete?: (taskId: string, columnId: string) => void;
  labelColors?: Record<string, string>;
  columnColors?: Record<string, string>;
  className?: string;
  allowAddTask?: boolean;
  selectableColumnIds?: string[];
  selectedTaskIds?: Set<string>;
  onTaskSelect?: (taskId: string) => void;
}

const defaultLabelColors: Record<string, string> = {
  tekst: "bg-blue-500",
  grafika: "bg-pink-500",
  video: "bg-violet-500",
  winner: "bg-emerald-500",
  loser: "bg-red-500",
  paused: "bg-amber-500",
};

const defaultColumnColors: Record<string, string> = {
  pomysly: "bg-slate-500",
  "do-nagrania": "bg-blue-500",
  "do-montazu": "bg-indigo-500",
  "gotowe-do-testu": "bg-cyan-500",
  "w-trakcie-testu": "bg-amber-500",
  zwycieskie: "bg-emerald-500",
  niedzialajace: "bg-red-500",
  "przestalo-dzialac": "bg-orange-500",
};

const PREVIEW_CAPTURE_SECONDS = 1.25;

function getDriveFileId(url: string): string | null {
  const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (fileIdMatch) return fileIdMatch[1];

  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return openMatch[1];

  return null;
}

function getDriveThumbnail(url: string): string | null {
  const fileId = getDriveFileId(url);
  return fileId ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w1600` : null;
}

function resolveImagePreviewUrl(url: string): string {
  return isDriveUrl(url) ? (getDriveThumbnail(url) ?? url) : url;
}

function DrivePreviewFallback({ title }: { title: string }) {
  return (
    <div className="mb-1.5 rounded overflow-hidden border border-white/[0.06] bg-white/[0.03] p-1.5">
      <div className="flex h-24 flex-col items-center justify-center gap-1 rounded bg-white/[0.02] text-white/35">
        <HardDrive size={18} />
        <span className="text-[10px] uppercase tracking-wide">Drive</span>
        <span className="max-w-full truncate px-2 text-[10px] text-white/25">{title}</span>
      </div>
    </div>
  );
}

function PreviewImage({ src, alt, title }: { src: string; alt: string; title: string }) {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError && isDriveUrl(src)) {
    return <DrivePreviewFallback title={title} />;
  }

  return (
    <div className="mb-1.5 rounded overflow-hidden border border-white/[0.06] bg-white/[0.03] p-1.5">
      <img
        src={src}
        alt={alt}
        className="w-full h-24 object-contain"
        loading="lazy"
        onError={() => setHasError(true)}
      />
    </div>
  );
}

function getYoutubeThumbnail(url: string): string | null {
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
}

function isDriveUrl(url: string): boolean {
  return url.includes("drive.google.com");
}

function isDropboxUrl(url: string): boolean {
  return url.includes("dropbox.com");
}

function isYoutubeUrl(url: string): boolean {
  return url.includes("youtube.com") || url.includes("youtu.be");
}

function isDirectVideoUrl(url: string): boolean {
  return !isDriveUrl(url) && !isDropboxUrl(url) && !isYoutubeUrl(url);
}

function getTaskMediaBadges(task: KanbanTask): Array<{ label: string; tone: string }> {
  if (!task.fileUrl) {
    return task.thumbnailUrl || task.imageUrl
      ? [{ label: "Thumbnail", tone: "bg-white/[0.06] text-white/45" }]
      : [];
  }

  if (isDriveUrl(task.fileUrl)) {
    return [
      { label: "Drive", tone: "bg-sky-500/15 text-sky-200/80" },
      { label: "Import for sync", tone: "bg-white/[0.06] text-white/45" },
    ];
  }

  if (isDropboxUrl(task.fileUrl)) {
    return [
      { label: "Dropbox", tone: "bg-cyan-500/15 text-cyan-200/80" },
      { label: "Import for sync", tone: "bg-white/[0.06] text-white/45" },
    ];
  }

  if (isYoutubeUrl(task.fileUrl)) {
    return [
      { label: "YouTube", tone: "bg-red-500/15 text-red-200/80" },
      { label: "Sync review", tone: "bg-emerald-500/15 text-emerald-200/80" },
    ];
  }

  return [
    { label: "Uploaded", tone: "bg-emerald-500/15 text-emerald-200/80" },
    { label: "Sync review", tone: "bg-emerald-500/15 text-emerald-200/80" },
  ];
}

function KanbanMediaPreview({ task }: { task: KanbanTask }) {
  const imageSrc = task.imageUrl || task.thumbnailUrl;

  if (imageSrc) {
    return (
      <PreviewImage src={resolveImagePreviewUrl(imageSrc)} alt={task.title} title={task.title} />
    );
  }

  if (!task.fileUrl) return null;

  const driveThumbnail = isDriveUrl(task.fileUrl) ? getDriveThumbnail(task.fileUrl) : null;
  if (driveThumbnail) {
    return <PreviewImage src={driveThumbnail} alt={task.title} title={task.title} />;
  }

  const youtubeThumbnail = isYoutubeUrl(task.fileUrl) ? getYoutubeThumbnail(task.fileUrl) : null;
  if (youtubeThumbnail) {
    return (
      <div className="mb-1.5 rounded overflow-hidden border border-white/[0.06] bg-white/[0.03] p-1.5">
        <img
          src={youtubeThumbnail}
          alt={task.title}
          className="w-full h-24 object-contain"
          loading="lazy"
        />
      </div>
    );
  }

  if (!isDirectVideoUrl(task.fileUrl)) {
    return (
      <div className="mb-1.5 flex h-24 items-center justify-center rounded border border-white/[0.06] bg-white/[0.03] text-white/25">
        <Film size={18} />
      </div>
    );
  }

  return <KanbanVideoFramePreview src={task.fileUrl} title={task.title} />;
}

function KanbanVideoFramePreview({ src, title }: { src: string; title: string }) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isReady, setIsReady] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setIsReady(false);
    setHasError(false);

    const handleLoadedMetadata = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        setIsReady(true);
        return;
      }

      const seekTarget = Math.min(PREVIEW_CAPTURE_SECONDS, Math.max(0.1, video.duration / 3));

      try {
        video.currentTime = seekTarget;
      } catch {
        setIsReady(true);
      }
    };

    const handleSeeked = () => {
      video.pause();
      setIsReady(true);
    };

    const handleLoadedData = () => {
      if (video.currentTime < 0.05) {
        setIsReady(true);
      }
    };

    const handleError = () => {
      setHasError(true);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("error", handleError);

    video.load();

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("error", handleError);
    };
  }, [src]);

  if (hasError) {
    return (
      <div className="mb-1.5 flex h-24 items-center justify-center rounded border border-white/[0.06] bg-white/[0.03] text-white/25">
        <Film size={18} />
      </div>
    );
  }

  return (
    <div className="mb-1.5 rounded overflow-hidden border border-white/[0.06] bg-white/[0.03] p-1.5">
      <div className="relative flex h-24 items-center justify-center">
        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center text-white/20">
            <Film size={18} />
          </div>
        )}
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          preload="metadata"
          className={cn(
            "h-full w-full object-contain pointer-events-none",
            !isReady && "opacity-0"
          )}
          aria-label={title}
        />
      </div>
    </div>
  );
}

export function KanbanBoard({
  columns: initialColumns,
  onColumnsChange,
  onTaskMove,
  onTaskAdd,
  onTaskClick,
  onTaskDelete,
  labelColors = defaultLabelColors,
  columnColors = defaultColumnColors,
  className,
  allowAddTask = true,
  selectableColumnIds,
  selectedTaskIds,
  onTaskSelect,
}: KanbanBoardProps) {
  const { t } = useTranslation();
  const [columns, setColumns] = React.useState<KanbanColumn[]>(initialColumns);
  const [draggedTask, setDraggedTask] = React.useState<{
    task: KanbanTask;
    sourceColumnId: string;
  } | null>(null);
  const dragMovedRef = React.useRef(false);
  const [dropTarget, setDropTarget] = React.useState<string | null>(null);
  const [addingCardTo, setAddingCardTo] = React.useState<string | null>(null);
  const [newCardTitle, setNewCardTitle] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  React.useEffect(() => {
    if (addingCardTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [addingCardTo]);

  const handleDragStart = (task: KanbanTask, columnId: string) => {
    dragMovedRef.current = false;
    setDraggedTask({ task, sourceColumnId: columnId });
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    dragMovedRef.current = true;
    setDropTarget(columnId);
  };

  const handleDrop = (targetColumnId: string) => {
    dragMovedRef.current = false;
    if (!draggedTask || draggedTask.sourceColumnId === targetColumnId) {
      setDraggedTask(null);
      setDropTarget(null);
      return;
    }

    const newColumns = columns.map((col) => {
      if (col.id === draggedTask.sourceColumnId) {
        return { ...col, tasks: col.tasks.filter((t) => t.id !== draggedTask.task.id) };
      }
      if (col.id === targetColumnId) {
        return { ...col, tasks: [...col.tasks, draggedTask.task] };
      }
      return col;
    });

    setColumns(newColumns);
    onColumnsChange?.(newColumns);
    onTaskMove?.(draggedTask.task.id, draggedTask.sourceColumnId, targetColumnId);
    setDraggedTask(null);
    setDropTarget(null);
  };

  const handleAddCard = (columnId: string) => {
    if (!newCardTitle.trim()) return;

    const newTask: KanbanTask = {
      id: `task-${Date.now()}`,
      title: newCardTitle.trim(),
      labels: [],
    };

    const newColumns = columns.map((col) =>
      col.id === columnId ? { ...col, tasks: [...col.tasks, newTask] } : col
    );

    setColumns(newColumns);
    onColumnsChange?.(newColumns);
    onTaskAdd?.(columnId, newCardTitle.trim());
    setNewCardTitle("");
    setAddingCardTo(null);
  };

  const getColumnColor = (columnId: string) => columnColors[columnId] || "bg-slate-500";
  const getLabelColor = (label: string) => labelColors[label] || "bg-slate-500";

  return (
    <div className={cn("flex gap-4 overflow-x-auto pb-4 h-full", className)}>
      {columns.map((column) => {
        const isDropActive = dropTarget === column.id && draggedTask?.sourceColumnId !== column.id;

        return (
          <div
            key={column.id}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDrop={() => handleDrop(column.id)}
            onDragLeave={() => setDropTarget(null)}
            className={cn(
              "min-w-[260px] max-w-[260px] rounded-xl p-3 transition-all duration-200 flex flex-col",
              "bg-white/[0.03] border",
              isDropActive ? "border-red-500/50 border-dashed bg-red-500/5" : "border-white/[0.06]"
            )}
          >
            {/* Column Header */}
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "h-2.5 w-2.5 rounded-full flex-shrink-0",
                    getColumnColor(column.id)
                  )}
                />
                <h2 className="text-xs font-semibold text-white/80 truncate max-w-[170px]">
                  {column.title}
                </h2>
                <span className="rounded-full bg-white/[0.08] px-1.5 py-0 text-[10px] font-medium text-white/40">
                  {column.tasks.length}
                </span>
                {(() => {
                  const pending = column.tasks.filter((t) => t.approvalStatus === "pending").length;
                  if (pending === 0) return null;
                  return (
                    <span
                      title={`${pending} do akceptacji`}
                      className="rounded-full bg-amber-500/15 px-1.5 py-0 text-[10px] font-medium text-amber-200 border border-amber-500/30"
                    >
                      ⏳ {pending}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Tasks */}
            <div className="flex flex-1 min-h-[200px] flex-col gap-2 overflow-y-auto">
              {column.tasks.map((task) => {
                const isDragging = draggedTask?.task.id === task.id;
                const isSelectable = selectableColumnIds?.includes(column.id) && onTaskSelect;
                const isSelected = selectedTaskIds?.has(task.id) ?? false;

                return (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={() => handleDragStart(task, column.id)}
                    onDragEnd={() => {
                      setDraggedTask(null);
                      dragMovedRef.current = false;
                    }}
                    onClick={() => {
                      if (!dragMovedRef.current && onTaskClick) onTaskClick(task, column.id);
                    }}
                    className={cn(
                      "group relative cursor-pointer rounded-lg border bg-white/[0.04] p-3 shadow-sm transition-all duration-150",
                      "hover:border-white/[0.12] hover:bg-white/[0.06] active:cursor-grabbing",
                      isDragging && "rotate-1 opacity-40",
                      isSelected ? "border-red-500 ring-1 ring-red-500/30" : "border-white/[0.06]"
                    )}
                  >
                    {/* Delete (X) — removes this record from the pipeline. */}
                    {onTaskDelete && !isSelectable && (
                      <button
                        type="button"
                        title={t("contentPipeline.deleteTask")}
                        aria-label={t("contentPipeline.deleteTask")}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskDelete(task.id, column.id);
                        }}
                        className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-white/30 opacity-0 transition-all hover:border-red-500/40 hover:bg-red-500/15 hover:text-red-300 group-hover:opacity-100"
                      >
                        <X size={13} />
                      </button>
                    )}

                    {(() => {
                      const mediaBadges = getTaskMediaBadges(task);
                      return mediaBadges.length > 0 ? (
                        <div className="mb-1.5 flex flex-wrap gap-1">
                          {mediaBadges.map((badge) => (
                            <span
                              key={`${task.id}-${badge.label}`}
                              className={cn(
                                "rounded-full px-1.5 py-0.5 text-[9px] font-medium",
                                badge.tone
                              )}
                            >
                              {badge.label}
                            </span>
                          ))}
                        </div>
                      ) : null;
                    })()}

                    {/* Selection checkbox */}
                    {isSelectable && (
                      <div
                        className="mb-1.5 flex items-center gap-2 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskSelect(task.id);
                        }}
                      >
                        <div
                          className={cn(
                            "w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all",
                            isSelected
                              ? "bg-red-500 border-red-500"
                              : "border-white/30 bg-transparent"
                          )}
                        >
                          {isSelected && <span className="text-white text-[9px]">✓</span>}
                        </div>
                        <span className="text-[9px] text-white/30">
                          {isSelected ? "Zaznaczono" : "Zaznacz"}
                        </span>
                      </div>
                    )}
                    {/* Labels */}
                    {task.labels && task.labels.length > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1">
                        {task.labels.map((label) => (
                          <span
                            key={label}
                            className={cn(
                              "rounded px-1.5 py-0 text-[9px] font-semibold uppercase text-white/90",
                              getLabelColor(label)
                            )}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Thumbnail */}
                    <KanbanMediaPreview task={task} />

                    <h3 className="text-sm font-medium text-white/80 leading-snug line-clamp-2">
                      {task.title}
                    </h3>

                    {task.approvalStatus && (
                      <div className="mt-1.5">
                        <span
                          className={cn(
                            "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[9px] font-semibold border",
                            task.approvalStatus === "approved" &&
                              "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
                            task.approvalStatus === "pending" &&
                              "border-amber-500/40 bg-amber-500/10 text-amber-200",
                            task.approvalStatus === "rejected" &&
                              "border-red-500/40 bg-red-500/10 text-red-200"
                          )}
                        >
                          {task.approvalStatus === "approved" && "✓ Zatwierdzone"}
                          {task.approvalStatus === "pending" && "⏳ Do akceptacji"}
                          {task.approvalStatus === "rejected" && "✕ Odrzucone"}
                        </span>
                      </div>
                    )}

                    {task.description && (
                      <p className="mt-1 text-[10px] text-white/40 line-clamp-2">
                        {task.description}
                      </p>
                    )}
                    {task.createdAt &&
                      (() => {
                        const created = new Date(task.createdAt);
                        const ageMs = Date.now() - created.getTime();
                        const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
                        let label: string;
                        let stale = false;
                        if (ageDays < 1) label = "dziś";
                        else if (ageDays === 1) label = "wczoraj";
                        else if (ageDays < 30) {
                          label = `${ageDays} dni`;
                          stale = ageDays >= 14;
                        } else {
                          label = `${Math.floor(ageDays / 30)} mies.`;
                          stale = true;
                        }
                        return (
                          <span
                            className={cn(
                              "mt-1 inline-block text-[9px]",
                              stale ? "text-amber-300/70" : "text-white/30"
                            )}
                          >
                            {stale && "⏳ "}
                            {label}
                          </span>
                        );
                      })()}
                  </div>
                );
              })}

              {/* Add Card */}
              {allowAddTask && (
                <>
                  {addingCardTo === column.id ? (
                    <div className="rounded-lg border border-white/[0.08] bg-white/[0.04] p-3">
                      <input
                        ref={inputRef}
                        type="text"
                        value={newCardTitle}
                        onChange={(e) => setNewCardTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddCard(column.id);
                          if (e.key === "Escape") {
                            setAddingCardTo(null);
                            setNewCardTitle("");
                          }
                        }}
                        placeholder={t("contentPipeline.addCreationPlaceholder")}
                        className="mb-2 w-full border-none bg-transparent text-xs text-white/80 outline-none placeholder:text-white/30"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAddCard(column.id)}
                          className="rounded bg-red-600 px-2.5 py-1 text-[10px] font-medium text-white transition-colors hover:bg-red-500"
                        >
                          {t("contentPipeline.add")}
                        </button>
                        <button
                          onClick={() => {
                            setAddingCardTo(null);
                            setNewCardTitle("");
                          }}
                          className="rounded px-2 py-1 text-[10px] text-white/40 transition-colors hover:bg-white/[0.06] hover:text-white/60"
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingCardTo(column.id)}
                      className="flex w-full items-center justify-center gap-1 rounded-lg p-1.5 text-[10px] text-white/30 transition-colors hover:bg-white/[0.04] hover:text-white/50"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      {t("contentPipeline.addCreation")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
